import React, { useState, useRef } from 'react';
import { User, Camera, X, Save, Loader2, Shield, CreditCard, ExternalLink, Lock, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import ThemeCustomizer from '@/components/ThemeCustomizer';
import SecuritySettings from '@/components/SecuritySettings';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useFeatures } from '@/context/FeaturesContext';
import { PricingModal } from '@/components/UpgradeBanner';
import axios from 'axios';
import { API } from '@/utils/api';

// Subscription Management Component
const SubscriptionManager = ({ user }) => {
  const [loading, setLoading] = useState(false);

  const openCustomerPortal = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/stripe/create-portal`);
      if (response.data.url) {
        // Use location.href for mobile compatibility (window.open is blocked on async calls)
        window.location.href = response.data.url;
      } else {
        toast.error('Unable to open subscription portal');
      }
    } catch (error) {
      toast.error('Failed to open subscription portal');
    } finally {
      setLoading(false);
    }
  };

  const tierLabels = {
    free: 'Free',
    premium: 'Premium',
    founding_member: 'Founding Member',
    enterprise: 'Enterprise',
  };

  const tierColors = {
    free: 'bg-gray-500',
    premium: 'bg-purple-500',
    founding_member: 'bg-amber-500',
    enterprise: 'bg-blue-500',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 rounded-lg" style={{ background: 'var(--theme-background-secondary)' }}>
        <div>
          <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>Current Plan</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded-full text-xs text-white ${tierColors[user?.membership_tier] || 'bg-gray-500'}`}>
              {tierLabels[user?.membership_tier] || 'Free'}
            </span>
          </div>
        </div>
        {/* Show Manage Subscription for paid users */}
        {(user?.membership_tier === 'premium' || user?.membership_tier === 'enterprise') && (
          <Button
            onClick={openCustomerPortal}
            disabled={loading}
            size="sm"
            style={{ background: 'var(--theme-primary)', color: 'white' }}
            data-testid="manage-subscription-settings-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
            Manage Subscription
          </Button>
        )}
        {user?.membership_tier === 'founding_member' && (
          <span className="text-xs px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">
            Lifetime Access
          </span>
        )}
      </div>

      {/* Premium/Enterprise: Info about what they can do */}
      {(user?.membership_tier === 'premium' || user?.membership_tier === 'enterprise') && (
        <p className="text-xs text-center" style={{ color: 'var(--theme-text-muted)' }}>
          Update payment method, view billing history, or cancel anytime.
        </p>
      )}

      {/* Founding Member: Thank you message */}
      {user?.membership_tier === 'founding_member' && (
        <p className="text-sm text-center" style={{ color: 'var(--theme-text-muted)' }}>
          You have lifetime premium access as a Founding Member. Thank you for your support!
        </p>
      )}

      {/* Free users: Show upgrade prompt */}
      {user?.membership_tier === 'free' && (
        <p className="text-sm text-center" style={{ color: 'var(--theme-text-muted)' }}>
          Upgrade to Premium for unlimited access to all features.
        </p>
      )}
    </div>
  );
};

const ProfileEditor = ({ user, onClose, onUpdate, clientDefaultTheme = null }) => {
  const { logout } = useAuth();
  const { isFeatureEnabled } = useFeatures();
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [displayName, setDisplayName] = useState(user?.display_name || user?.name || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || '');
  const [picture, setPicture] = useState(user?.picture || '');
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'theme', 'security', or 'subscription'
  const { theme, skinTone, isGradientMode } = useTheme();
  
  // B2B users should NOT see subscription/billing options
  const isB2BUser = !!user?.client_id;
  
  // Check if themes are enabled for this user's tier
  // Only check 'themes' key - custom_themes is legacy
  const themesEnabled = isFeatureEnabled('themes');
  
  // Convert API paths to full URLs for preview
  const getFullPictureUrl = (pic) => {
    if (!pic) return '';
    if (pic.startsWith('/api')) {
      return `${API.replace('/api', '')}${pic}`;
    }
    return pic;
  };
  const [previewUrl, setPreviewUrl] = useState(getFullPictureUrl(user?.picture) || '');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result;
      setPreviewUrl(base64);
      setPicture(base64);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/user/profile`, {
        first_name: firstName,
        last_name: lastName,
        display_name: displayName,
        phone_number: phoneNumber,
        picture: picture !== user?.picture ? picture : undefined,
        theme: theme, // Include theme colors
        emoji_skin_tone: skinTone, // Include emoji skin tone
      });

      const data = response.data;

      toast.success('Profile updated successfully!');
      onUpdate(data);
      onClose();
    } catch (error) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div 
        className="rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{
          background: 'var(--theme-background)',
          border: '2px solid rgba(var(--theme-primary-rgb), 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ color: 'var(--theme-text)' }}>Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--theme-background-secondary)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" style={{ color: 'var(--theme-text-secondary)' }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-[var(--theme-background-secondary)]/30 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${
              activeTab === 'profile' 
                ? 'text-white shadow-lg' 
                : 'hover:text-white'
            }`}
            style={{ 
              color: activeTab !== 'profile' ? 'var(--theme-text-secondary)' : undefined,
              background: activeTab === 'profile' ? 'var(--theme-button-bg)' : undefined
            }}
          >
            Profile
          </button>
          <button
            onClick={() => setActiveTab('theme')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1 ${
              activeTab === 'theme' 
                ? 'text-white shadow-lg' 
                : 'hover:text-white'
            }`}
            style={{ 
              color: activeTab !== 'theme' ? 'var(--theme-text-secondary)' : undefined,
              background: activeTab === 'theme' ? 'var(--theme-button-bg)' : undefined
            }}
          >
            Theme
            {!themesEnabled && <Lock className="w-3 h-3 ml-1 text-purple-400" />}
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1 ${
              activeTab === 'security' 
                ? 'text-white shadow-lg' 
                : 'hover:text-white'
            }`}
            style={{ 
              color: activeTab !== 'security' ? 'var(--theme-text-secondary)' : undefined,
              background: activeTab === 'security' ? 'var(--theme-button-bg)' : undefined
            }}
          >
            <Shield className="w-4 h-4" />
            Security
          </button>
          {/* Subscription/Billing tab - hide for B2B users */}
          {!isB2BUser && (
            <button
              onClick={() => setActiveTab('subscription')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1 ${
                activeTab === 'subscription' 
                  ? 'text-white shadow-lg' 
                  : 'hover:text-white'
              }`}
              style={{ 
                color: activeTab !== 'subscription' ? 'var(--theme-text-secondary)' : undefined,
                background: activeTab === 'subscription' ? 'var(--theme-button-bg)' : undefined
              }}
            >
              <CreditCard className="w-4 h-4" />
              Billing
            </button>
          )}
        </div>

        {activeTab === 'profile' ? (
          <>
            {/* Profile Photo */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative">
                <div 
                  className="w-24 h-24 rounded-full overflow-hidden bg-[var(--theme-background-secondary)] flex items-center justify-center"
                  style={{ border: '2px solid rgba(var(--theme-border-rgb, var(--theme-primary-rgb)), 0.5)' }}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-12 h-12" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }} />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-2 rounded-full hover:opacity-90 transition-opacity"
                  style={{ background: 'var(--theme-button-bg)' }}
                >
                  <Camera className="w-4 h-4 text-white" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>Click camera to upload photo</p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-secondary)' }}>First Name</label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bg-white border-[var(--theme-border)]/30 text-gray-900 placeholder-gray-400"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-secondary)' }}>Last Name</label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="bg-white border-[var(--theme-border)]/30 text-gray-900 placeholder-gray-400"
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-secondary)' }}>Display Name</label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="bg-white border-[var(--theme-border)]/30 text-gray-900 placeholder-gray-400"
                  placeholder="How you want to be called"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>This is the name shown in your journal</p>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--theme-text-secondary)' }}>Phone Number</label>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="bg-white border-[var(--theme-border)]/30 text-gray-900 placeholder-gray-400"
                  placeholder="(555) 123-4567"
                />
                <p className="text-xs mt-1" style={{ color: 'var(--theme-text-secondary)', opacity: 0.5 }}>Optional - for account recovery</p>
              </div>
            </div>
          </>
        ) : activeTab === 'theme' ? (
          themesEnabled ? (
            <ThemeCustomizer onSave={handleSave} saving={saving} clientDefaultTheme={clientDefaultTheme} />
          ) : (
            <div className="text-center py-8">
              <div 
                className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}
              >
                <Lock className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
                Custom Themes
              </h3>
              {isB2BUser ? (
                <p className="text-sm mb-6" style={{ color: 'var(--theme-text-muted)' }}>
                  Theme customization is not available for your account.
                  <br />Contact your administrator for more information.
                </p>
              ) : (
                <>
                  <p className="text-sm mb-6" style={{ color: 'var(--theme-text-muted)' }}>
                    Personalize your journal with custom colors, themes, and styles.
                    <br />Upgrade to Premium to unlock this feature.
                  </p>
                  <Button
                    onClick={() => setShowPricingModal(true)}
                    className="font-semibold"
                    style={{
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                      color: 'white',
                    }}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Premium
                  </Button>
                </>
              )}
            </div>
          )
        ) : activeTab === 'subscription' && !isB2BUser ? (
          <SubscriptionManager user={user} />
        ) : (
          <SecuritySettings onLogout={() => { onClose(); logout(); }} />
        )}

        {/* Actions - hide for security and subscription tabs */}
        {activeTab !== 'security' && activeTab !== 'subscription' && (
          <div className="flex gap-3 mt-6">
            <Button
              onClick={onClose}
              variant="ghost"
              className="flex-1 hover:bg-[var(--theme-background-secondary)]"
              style={{ color: 'var(--theme-text-secondary)' }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 text-white hover:opacity-90"
              style={{ background: 'var(--theme-button-bg)' }}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        )}
      </div>
      
      {/* Pricing Modal - Only for non-B2B users */}
      {!isB2BUser && showPricingModal && (
        <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />
      )}
    </div>
  );
};

export default ProfileEditor;
